import { useCallback } from "react";
import apiClient from "../../api/axios";
import { ExtractedContract } from "../../types/contract";
import { isSourceValue, isCoOccupantInfo } from "./utils";

export const useContractEdit = (
  generalContracts: ExtractedContract[],
  setGeneralContracts: (contracts: ExtractedContract[]) => void,
  finalContracts: ExtractedContract[],
  setFinalContracts: (contracts: ExtractedContract[]) => void,
  selectedContract: ExtractedContract | null,
  setChangedContractIds: (updater: (prev: Set<number>) => Set<number>) => void,
  setSnackbar: (snackbar: { open: boolean; message: string; severity?: "success" | "info" | "warning" | "error" }) => void
) => {
  const handleGeneralContractChange = useCallback((
    index: number,
    field: keyof ExtractedContract,
    value: any
  ) => {
    const updatedContracts = [...generalContracts];
    const contract = { ...updatedContracts[index] };
    const basicFields = [
      "filename",
      "property_name",
      "room_name",
      "lessee_name",
      "document_type",
    ];
    if (basicFields.includes(field as string)) {
      (contract as any)[field] = value;
    } else {
      const fieldValue = contract[field];
      if (isSourceValue(fieldValue)) {
        fieldValue.value = value;
      } else {
        (contract as any)[field] = { value: value, source_document: null };
      }
    }
    updatedContracts[index] = contract;
    setGeneralContracts(updatedContracts);
  }, [generalContracts, setGeneralContracts]);

  const handleDeleteTmpRecord = useCallback(async (index: number, recordId: number) => {
    const contract = generalContracts[index];
    const filename = contract.filename.split(/[/／]/).pop() || contract.filename;

    if (!window.confirm(`「${filename}」を削除してもよろしいですか？\n\nこの操作は取り消せません。`)) {
      return;
    }

    try {
      await apiClient.delete(`/api/tmp-records/${recordId}`);

      // Remove the contract from the list
      const updatedContracts = generalContracts.filter((_, i) => i !== index);
      setGeneralContracts(updatedContracts);

      setSnackbar({ open: true, message: `「${filename}」を削除しました。` });
    } catch (error: any) {
      console.error("Error deleting tmp record:", error);
      setSnackbar({
        open: true,
        message: `削除に失敗しました: ${error.response?.data?.detail || error.message}`,
      });
    }
  }, [generalContracts, setGeneralContracts, setSnackbar]);

  const handleDetailChange = useCallback((
    field: keyof ExtractedContract,
    value: string
  ) => {
    if (!selectedContract || selectedContract.id === undefined) return;

    // Track the ID of the changed contract
    setChangedContractIds((prev) => new Set(prev).add(selectedContract.id!));

    const newFinalContracts = finalContracts.map((c) => {
      if (
        c.property_name === selectedContract.property_name &&
        c.room_name === selectedContract.room_name &&
        c.lessee_name === selectedContract.lessee_name
      ) {
        const updatedContract = { ...c };
        const fieldValue = updatedContract[field];

        if (isCoOccupantInfo(fieldValue)) {
          // This is now read-only in the UI, so no update logic needed here for now.
        } else if (isSourceValue(fieldValue)) {
          fieldValue.value = value;
        } else {
          (updatedContract as any)[field] = {
            value: value,
            source_document: "手動入力",
          };
        }
        return updatedContract;
      }
      return c;
    });
    setFinalContracts(newFinalContracts);
  }, [selectedContract, finalContracts, setFinalContracts, setChangedContractIds]);

  return {
    handleGeneralContractChange,
    handleDeleteTmpRecord,
    handleDetailChange,
  };
};